import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

// A birthday calendar in the app's own look (Nic 2026-10-25: the browser's
// date picker was "janky"). A birthday is decades back, so the header jumps:
// click "December 1986" for a grid of years, then months, then the day.
// Plain days only (YYYY-MM-DD), no time zone: a birthday is a date, not a
// moment. Nothing after `max` can be picked.

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FIRST_YEAR = 1900;

const ymdOf = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const parse = (v) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) && moment.utc(v, 'YYYY-MM-DD', true).isValid() ? v : null);

const CELL = 'flex h-[30px] items-center justify-center rounded-md border-0 text-[12.5px] tabular-nums';
const NAV =
  'inline-flex h-[26px] w-[26px] items-center justify-center rounded-md border-0 bg-transparent p-0 text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10';

export const BirthdayGrid = ({ value, max, onPick }) => {
  const picked = parse(value);
  const maxYmd = parse(max) || moment.utc().format('YYYY-MM-DD');
  const maxYear = Number(maxYmd.slice(0, 4));
  const start = picked || `${Math.min(1980, maxYear)}-01-01`;
  const [y, setY] = useState(Number(start.slice(0, 4)));
  const [m, setM] = useState(Number(start.slice(5, 7)) - 1);
  const [view, setView] = useState('days');
  const yearsRef = useRef(null);

  // The years grid opens scrolled to the year in view.
  useEffect(() => {
    if (view !== 'years') return;
    const el = yearsRef.current?.querySelector('[data-current="true"]');
    el?.scrollIntoView?.({ block: 'center' });
  }, [view]);

  const cells = useMemo(() => {
    const first = moment.utc([y, m, 1]);
    const lead = first.day();
    const days = first.daysInMonth();
    const out = Array.from({ length: lead }, () => null);
    for (let d = 1; d <= days; d += 1) out.push(ymdOf(y, m, d));
    while (out.length % 7) out.push(null);
    return out;
  }, [y, m]);

  const step = (n) => {
    const next = moment.utc([y, m, 1]).add(n, 'month');
    setY(next.year());
    setM(next.month());
  };
  const atMax = y > maxYear || (y === maxYear && m >= Number(maxYmd.slice(5, 7)) - 1);
  const atMin = y <= FIRST_YEAR && m === 0;

  return (
    <div className="w-[236px] select-none">
      <div className="mb-[6px] flex items-center justify-between">
        <button
          type="button"
          onClick={() => setView(view === 'days' ? 'years' : 'days')}
          className="rounded-md border-0 bg-transparent px-[6px] py-[2px] text-[13px] font-semibold text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:!bg-white/10"
        >
          {view === 'years' ? 'Pick a year' : view === 'months' ? y : `${moment.utc([y, m, 1]).format('MMMM')} ${y}`}
        </button>
        {view === 'days' ? (
          <span className="flex gap-[2px]">
            <button type="button" aria-label="Previous month" disabled={atMin} onClick={() => step(-1)} className={NAV}>
              <ChevronLeft size={15} />
            </button>
            <button type="button" aria-label="Next month" disabled={atMax} onClick={() => step(1)} className={NAV}>
              <ChevronRight size={15} />
            </button>
          </span>
        ) : null}
      </div>

      {view === 'years' ? (
        <div ref={yearsRef} className="grid max-h-[212px] grid-cols-4 gap-[2px] overflow-y-auto [scrollbar-width:thin]">
          {Array.from({ length: maxYear - FIRST_YEAR + 1 }, (_, i) => maxYear - i).map((yr) => (
            <button
              key={yr}
              type="button"
              data-current={yr === y}
              onClick={() => {
                setY(yr);
                if (yr === maxYear && m > Number(maxYmd.slice(5, 7)) - 1) setM(Number(maxYmd.slice(5, 7)) - 1);
                setView('months');
              }}
              className={cn(
                CELL,
                yr === y
                  ? 'bg-slate-900 font-semibold text-white dark:!bg-slate-100 dark:!text-slate-900'
                  : 'bg-transparent text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:!bg-white/10',
              )}
            >
              {yr}
            </button>
          ))}
        </div>
      ) : view === 'months' ? (
        <div className="grid grid-cols-3 gap-[4px]">
          {MONTHS.map((label, i) => {
            const off = y === maxYear && i > Number(maxYmd.slice(5, 7)) - 1;
            return (
              <button
                key={label}
                type="button"
                disabled={off}
                onClick={() => {
                  setM(i);
                  setView('days');
                }}
                className={cn(
                  CELL,
                  'h-[38px]',
                  i === m
                    ? 'bg-slate-900 font-semibold text-white dark:!bg-slate-100 dark:!text-slate-900'
                    : 'bg-transparent text-slate-700 hover:bg-slate-100 disabled:opacity-30 dark:text-slate-200 dark:hover:!bg-white/10',
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 text-center text-[10.5px] font-semibold text-slate-400 dark:text-slate-500">
            {WEEKDAYS.map((w, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i} className="py-[2px]">
                {w}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-[1px]">
            {cells.map((day, i) =>
              day ? (
                <button
                  key={day}
                  type="button"
                  aria-label={moment.utc(day).format('MMMM D, YYYY')}
                  aria-pressed={day === picked}
                  disabled={day > maxYmd}
                  onClick={() => onPick(day)}
                  className={cn(
                    CELL,
                    day === picked
                      ? 'bg-sky-600 font-semibold text-white dark:!bg-sky-500'
                      : 'bg-transparent text-slate-700 hover:bg-slate-100 disabled:opacity-30 dark:text-slate-200 dark:hover:!bg-white/10',
                  )}
                >
                  {Number(day.slice(8))}
                </button>
              ) : (
                // eslint-disable-next-line react/no-array-index-key
                <span key={`b${i}`} />
              ),
            )}
          </div>
        </>
      )}
    </div>
  );
};

// The calendar button inside the birthday box (its right end).
const BirthdayCalendar = ({ value, max, onPick }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Pick from a calendar"
          className="inline-flex h-[18px] w-[20px] shrink-0 items-center justify-center rounded border-0 bg-transparent p-0 text-slate-400 hover:text-sky-600 dark:text-slate-500 dark:hover:text-sky-300"
        >
          <CalendarDays size={14} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-[10px]">
        {/* Remount on open so it starts on the birthday in the box. */}
        {open ? (
          <BirthdayGrid
            value={value}
            max={max}
            onPick={(day) => {
              onPick(day);
              setOpen(false);
            }}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

export default BirthdayCalendar;
